import React, { PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react'
import styled, { CSSProperties } from 'styled-components'
import { ScaleIn } from './Animations'
import { RADOM_COLORS } from '../util/Constants'
import { HoverInfoIcon } from '../icons/Info'

interface IProps extends PropsWithChildren {
  message: ReactElement | string
  disabled?: boolean
  style?: any
  infoIconStyle?: CSSProperties
}

const TooltipMessage = styled.div`
  position: fixed;
  top: 0px;
  padding: 12px 14px;
  border-radius: 12px;
  background-color: white;
  border: 1px solid #e7eaf0;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.14);
  font-size: 12.5px;
  width: max-content;
  max-width: 230px;
  z-index: 140;
  animation: ${ScaleIn} 0.2s;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-weight: 400;
  line-height: 1.45;
`

const Tooltip = (props: IProps): ReactElement => {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement | null>(null)
  const [top, setTop] = useState(0)
  const [left, setLeft] = useState(0)
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!container.current || !open || !tooltipRef.current) return

    const rect = container.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()

    const defaultLeft = rect.right + 12
    const overflowsRight = defaultLeft + tooltipRect.width > window.innerWidth
    const desiredLeft = overflowsRight
      ? rect.left - tooltipRect.width - 12
      : defaultLeft
    const desiredTop = rect.top + (rect.height / 2) - (tooltipRect.height / 2)

    const left = Math.max(12, Math.min(desiredLeft, window.innerWidth - tooltipRect.width - 12))
    const top = Math.max(12, Math.min(desiredTop, window.innerHeight - tooltipRect.height - 12))

    setTop(top)
    setLeft(left)
  }, [open])

  return <div
    ref={container}
    style={{ position: 'relative', fontSize: 0 }}
    onMouseEnter={() => !props.disabled && setOpen(true)}
    onMouseLeave={() => setOpen(false)}>
    {
      props.children
        ? props.children
        : <HoverInfoIcon style={props.infoIconStyle}
          width={20} />
    }
    {
      open &&
        <TooltipMessage ref={tooltipRef}
          style={{ top, left, ...props.style }}>
          {props.message}
        </TooltipMessage>
    }
  </div>
}

export default Tooltip
